import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Input, Radio, Select, Typography } from 'antd'
import type { z } from 'zod'
import { siteQuickCreateSchema, type siteQuickCreateOutputSchema } from '@super-mega/server/contracts/site'
import { GUANGZHOU_TIANHE_REGION_CODES } from '@/features/master-data/const/region-cascader.ts'
import { getRegionSnapshot } from '@/features/master-data/utils/region.ts'
import { useTRPC } from '@/trpc/react.ts'
import { RoomGenerationFields } from '../room/room-generation-fields.tsx'
import { buildRooms, type RoomGenerationValues } from '../room/room-generation.ts'
import { RegionCascader } from './region-cascader.tsx'
import type { SiteFormValues } from './site-editor-modal.tsx'

type QuickAddressValues = Omit<SiteFormValues, 'name' | 'note'> & RoomGenerationValues

export function SiteQuickCreateModal({
  onClose,
  onCreated
}: {
  onClose: () => void
  onCreated: (site: z.infer<typeof siteQuickCreateOutputSchema>['site']) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<QuickAddressValues>()
  const [open, setOpen] = useState(true)
  const [organizationKeyword, setOrganizationKeyword] = useState('')
  const operationMode =
    Form.useWatch<QuickAddressValues['operationMode'] | undefined>('operationMode', form) ?? 'selfOperated'
  const organizations = useQuery(
    trpc.lookup.organizations.queryOptions({ purpose: 'site.read', keyword: organizationKeyword })
  )
  const create = useMutation(trpc.site.quickCreate.mutationOptions())

  const save = async (values: QuickAddressValues) => {
    try {
      const input = {
        site: {
          ...getRegionSnapshot(values.regionCodes),
          detailAddress: values.detailAddress.trim(),
          organizationId: values.operationMode === 'selfOperated' ? values.organizationId : undefined,
          operationMode: values.operationMode
        },
        rooms: {
          rooms: buildRooms(values),
          floor: values.floor?.trim() || null,
          roomType: values.roomType
        }
      }
      const validation = siteQuickCreateSchema.safeParse(input)
      if (!validation.success) {
        void message.error(validation.error.issues[0]?.message ?? '请检查场地与房间信息')
        return false
      }
      const result = await create.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries(trpc.site.list.queryFilter()),
        queryClient.invalidateQueries(trpc.site.options.queryFilter()),
        queryClient.invalidateQueries(trpc.room.list.queryFilter()),
        queryClient.invalidateQueries(trpc.room.siteOptions.queryFilter()),
        queryClient.invalidateQueries(trpc.room.recommend.queryFilter())
      ])
      void message.success(`地址已创建，已生成 ${result.created} 个房间`)
      onCreated(result.site)
      setOpen(false)
      return true
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '快速创建地址失败')
      return false
    }
  }

  return (
    <ModalForm<QuickAddressValues>
      form={form}
      name="site-quick-create"
      open={open}
      title="快速创建地址"
      width="64rem"
      layout="vertical"
      initialValues={{
        operationMode: 'selfOperated',
        regionCodes: [...GUANGZHOU_TIANHE_REGION_CODES],
        mode: 'range',
        roomType: 'office',
        padStart: 1
      }}
      disabled={create.isPending}
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !create.isPending,
        keyboard: !create.isPending,
        afterClose: onClose
      }}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !create.isPending) {
          setOpen(false)
        }
      }}
      onFinish={save}
      scrollToFirstError
      submitter={{
        resetButtonProps: { disabled: create.isPending },
        searchConfig: { resetText: '取消', submitText: '创建地址与房间' },
        submitButtonProps: { loading: create.isPending }
      }}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <section aria-label="场地信息" className="min-w-0">
          <Typography.Title level={5} className="mt-0!">
            场地信息
          </Typography.Title>
          <Form.Item
            label="归属主体"
            name="organizationId"
            rules={operationMode === 'selfOperated' ? [{ required: true, message: '请选择经营主体' }] : []}
          >
            <Select
              disabled={operationMode === 'agent'}
              loading={organizations.isFetching}
              options={(organizations.data?.data ?? []).map((item) => ({
                label: item.name,
                value: item.id,
                disabled: item.isDisable
              }))}
              placeholder={operationMode === 'agent' ? '代理场地不绑定经营主体' : '请选择经营主体'}
              showSearch={{ filterOption: false, onSearch: setOrganizationKeyword }}
            />
          </Form.Item>
          {organizations.isError && (
            <Alert
              className="mb-4!"
              type="error"
              showIcon
              title="经营主体加载失败"
              action={
                <Button type="link" onClick={() => void organizations.refetch()}>
                  重试
                </Button>
              }
            />
          )}
          <Form.Item label="经营类型" name="operationMode" rules={[{ required: true }]}>
            <Radio.Group
              optionType="button"
              options={[
                { label: '自营', value: 'selfOperated' },
                { label: '代理', value: 'agent' }
              ]}
              onChange={() => form.setFieldValue('organizationId', undefined)}
            />
          </Form.Item>
          <Form.Item label="省市区" name="regionCodes" rules={[{ required: true, message: '请选择省、市、区县' }]}>
            <RegionCascader />
          </Form.Item>
          <Form.Item
            label="详细地址"
            name="detailAddress"
            rules={[{ required: true, whitespace: true, message: '请输入详细地址' }, { max: 500 }]}
          >
            <Input.TextArea rows={3} maxLength={500} placeholder="街道、门牌号、楼栋及房号" />
          </Form.Item>
          <Typography.Paragraph type="secondary">场地名称直接使用详细地址。</Typography.Paragraph>
        </section>
        <section aria-label="批量创建房源" className="min-w-0">
          <Typography.Title level={5} className="mt-0!">
            批量创建房源
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            房源自动归属本次新建场地，初始状态为未使用（空闲）。单次最多生成 500 个。
          </Typography.Paragraph>
          <RoomGenerationFields />
        </section>
      </div>
    </ModalForm>
  )
}
